import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { grey } from '@mui/material/colors';

import { DialogTitleWithClose, Loader } from 'ui';
import { getFlattenedIndustries } from 'selectors/common';
import { formatCurrency } from 'shared/functions';
import { ShopRelatedProductsData } from 'shared/types/shop';

import { shopViews } from '../shared/constants/shopViews';
import {
  addShopProductsToBasketAction,
  startShopProductFreeSubscriptionAction,
  startShopProductFreeTrialAction,
} from '../store/shopActions';
import { fetchShopRelatedSubscriptions } from '../services';
import { ShopProductFreeSubscriptionModal } from './ShopProduct/ShopProductFreeSubscriptionModal';
import { ShopProductTrialModal } from './ShopProduct/ShopProductTrialModal';
import { ShopProductsModal } from './ShopProductsModal';
import { ShopProduct } from '../shared/types/shopProduct';
import { ratingTypes } from '../../../shared/constants/ratingTypes';

const useStyles = makeStyles((theme) => ({
  title: {
    color: theme.palette.primary.main,
    paddingBottom: 10,
  },
  subtitle: {
    color: theme.palette.primary.main,
    paddingBottom: 18,
  },
  label: {
    color: theme.palette.primary.main,
  },
  value: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 3,
  },
  product: {
    borderBottomWidth: 1,
    borderBottomColor: grey[300],
    borderBottomStyle: 'solid',
  },
  basketButton: {
    height: 45,
    backgroundColor: '#ff8900',
    color: theme.palette.primary.contrastText,
    marginLeft: 20,
  },
  infoButton: {
    height: 45,
  },
  name: {
    maxWidth: 530,
    fontSize: 18,
    padding: '9px 0',
  },
  price: {
    backgroundColor: theme.palette.primary.main,
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#fff',
    fontSize: 16,
  },
}));

type ShopRelatedProductsModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  data: ShopRelatedProductsData;
  module?: ratingTypes;
};

type OpenById = Record<number, boolean>;

const ShopRelatedProductsModal: FC<ShopRelatedProductsModalProps> = ({
  open,
  data,
  onClose,
  onConfirm = () => {},
  module,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isFreeSubscriptionModalOpen, setIsFreeSubscriptionModalOpen] = useState<OpenById>({});

  const [isFreeTrialModalOpen, setIsFreeTrialModalOpen] = useState<OpenById>({});

  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState<OpenById>({});

  const [relatedSubscriptionIds, setRelatedSubscriptionIds] = useState<number[]>([]);
  const [relatedSubscriptions, setRelatedSubscriptions] = useState<ShopProduct[]>([]);

  const didUnmount = useRef(false);

  const allIndustries = useSelector(getFlattenedIndustries);

  const relatedIndustries = allIndustries.filter(({ code, name }) => {
    const industryList = data.countriesAndIndustries
      ? data.countriesAndIndustries.map(({ industries }) => industries).flat()
      : [];

    return industryList.includes(code) || industryList.includes(name);
  });

  const products: { data: ShopProduct[]; isPending: boolean } = useSelector(
    ({ shop }: any) => shop.products,
  );

  const fetchData = async () => {
    const countriesAndIndustries = (data.countriesAndIndustries || []).map(
      ({ industries, country }) => {
        const industryNames = relatedIndustries
          .filter(({ code, name }) => industries.includes(code) || industries.includes(name))
          .map(({ code }) => code);

        return {
          country,
          industry: industryNames || [],
        };
      },
    );

    const response = await fetchShopRelatedSubscriptions({
      category: data.category,
      productLines: data.productLine ? [data.productLine] : [],
      countriesAndIndustries,
      module:
        module &&
        {
          [ratingTypes.ESG_IMPACT]: 'ESG Impact Review',
          [ratingTypes.SENTIMENT]: 'Sentifi',
          [ratingTypes.CLIMATE_STRESS_TEST]: 'Climate Stress Test',
        }[module],
    });

    if (response && !didUnmount.current) {
      setRelatedSubscriptionIds(response.products);
    }
  };

  useEffect(() => {
    if (data.category) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.countriesAndIndustries, data.productLine, data.category]);

  useEffect(() => {
    if (relatedSubscriptions.length) return;

    setRelatedSubscriptions(products.data.filter(({ id }) => relatedSubscriptionIds.includes(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatedSubscriptionIds]);

  const getPrice = (product: any) => {
    const pricing = product.find(({ active }: any) => active);

    return pricing ? pricing.price : null;
  };

  const toggleSubscriptionModal = (id: number) => {
    setIsSubscriptionModalOpen((isOpenById) => ({ ...isOpenById, [id]: !isOpenById[id] }));
  };

  const toggleFreeTrialModal = (id: number) => {
    setIsFreeTrialModalOpen((isOpenById) => ({ ...isOpenById, [id]: !isOpenById[id] }));

    if (isFreeTrialModalOpen[id]) {
      onConfirm();
    }
  };

  const toggleFreeSubscriptionModal = (id: number) => {
    setIsFreeSubscriptionModalOpen((isOpenById) => ({ ...isOpenById, [id]: !isOpenById[id] }));

    if (isFreeSubscriptionModalOpen[id]) {
      onConfirm();
    }
  };

  const activateFreeSubscription = (id: number) => {
    dispatch(startShopProductFreeSubscriptionAction({ id }));
  };

  const addToBasket = (id: number) => {
    dispatch(addShopProductsToBasketAction({ ids: [id] }));

    navigate(`/subscriptions/?tab=${shopViews.basket}`);
  };

  const activateFreeTier = (id: number) => {
    dispatch(startShopProductFreeTrialAction({ id }));
  };

  const getIndustryName = () => (relatedIndustries.length ? relatedIndustries[0].name : 'All');
  const getSegmentName = () =>
    relatedIndustries.length ? relatedIndustries[0].industryName : 'All';

  const handleConfirmSubscription = (productId: number) => {
    activateFreeSubscription(productId);
  };

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  const renderBuyButton = (product: ShopProduct) => {
    if (product.isFreeTrial && !product.trialStarted) {
      return (
        <>
          <Button
            variant="outlined"
            color="primary"
            className={classes.infoButton}
            onClick={() => toggleFreeTrialModal(product.id)}
            disabled={product.purchased}
          >
            {product.purchased ? t('Subscribed') : t('Free Trial')}
          </Button>
          <ShopProductTrialModal
            open={Boolean(isFreeTrialModalOpen[product.id])}
            onConfirm={() => activateFreeTier(product.id)}
            onClose={() => toggleFreeTrialModal(product.id)}
            data={product as any}
          />
        </>
      );
    }

    const price = getPrice(product.pricing);

    if (price === 0) {
      return (
        <>
          <Button
            color="primary"
            variant="outlined"
            className={classes.infoButton}
            onClick={() => toggleFreeSubscriptionModal(product.id)}
          >
            {t('Get started')}
          </Button>
          <ShopProductFreeSubscriptionModal
            open={Boolean(isFreeSubscriptionModalOpen[product.id])}
            onConfirm={() => handleConfirmSubscription(product.id)}
            onClose={() => toggleFreeSubscriptionModal(product.id)}
            data={product as any}
          />
        </>
      );
    }

    return (
      <Button
        color="primary"
        variant="contained"
        className={classes.basketButton}
        onClick={() => addToBasket(product.id)}
      >
        {t('Add to basket')}
      </Button>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl">
      <DialogTitleWithClose className={classes.title} onClose={onClose}>
        <Typography variant="h6">
          <strong>{t('This content is part of')}</strong>
        </Typography>
      </DialogTitleWithClose>
      <DialogContent>
        <Box minWidth={900}>
          <Divider />
          <Box mt={4} mb={8} px={2}>
            <Grid container justifyContent="space-between">
              <Grid item>
                <Box className={classes.label}>{t('Category')}</Box>
                <Box className={classes.value}>{data?.category ? data.category : '-'}</Box>
              </Grid>
              <Grid item>
                <Box className={classes.label}>{t('Product line')}</Box>
                <Box className={classes.value}>{data?.productLine ? data.productLine : '-'}</Box>
              </Grid>
              <Grid item>
                <Box className={classes.label}>{t('Segment')}</Box>
                <Box className={classes.value}>{getSegmentName()}</Box>
              </Grid>
              <Grid item>
                <Box className={classes.label}>{t('Industry')}</Box>
                <Box className={classes.value}>
                  {relatedIndustries.length > 1 ? t('Various') : getIndustryName()}
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box mt={4}>
            <Typography variant="h6" className={classes.subtitle}>
              <strong>{t('To unlock subscribe to one of the following subscriptions')}</strong>
            </Typography>
            <Divider />
            <Box mt={2} mb={4}>
              {products.isPending ? (
                <Loader center />
              ) : (
                <Paper variant="elevation" elevation={3}>
                  {relatedSubscriptions.length ? (
                    relatedSubscriptions.map((product) => (
                      <Box p={3} className={classes.product} key={product.id}>
                        <Grid container justifyContent="space-between">
                          <Grid item>
                            <Typography className={classes.name}>{product.name}</Typography>
                          </Grid>
                          <Grid item>
                            <Box ml={2}>
                              <Box className={classes.price} p={2} mb={2}>
                                {getPrice(product.pricing) === 0
                                  ? t('Free of charge')
                                  : `${formatCurrency(getPrice(product.pricing), 'EUR')} ${
                                      product.validity
                                    }`}
                              </Box>
                              <Box display="flex">
                                <Box>
                                  <Button
                                    color="primary"
                                    variant="outlined"
                                    className={classes.infoButton}
                                    onClick={() => toggleSubscriptionModal(product.id)}
                                  >
                                    {t('More info')}
                                  </Button>
                                </Box>
                                <Box ml={1}>{renderBuyButton(product)}</Box>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                        <ShopProductsModal
                          products={relatedSubscriptions}
                          activeProduct={product.id}
                          open={Boolean(isSubscriptionModalOpen[product.id])}
                          onClose={() => toggleSubscriptionModal(product.id)}
                        />
                      </Box>
                    ))
                  ) : (
                    <Box p={4}>
                      <Typography variant="h6">{t('No products found')}</Typography>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export { ShopRelatedProductsModal };
